"use client";

import { useState, useCallback, useEffect } from "react";
import { Search, Plus, ChevronRight, MoreHorizontal, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  fetchEmployees,
  fetchDepartments,
} from "@/lib/supabase/employees-actions";
import {
  EMPLOYEE_STATUS_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  getInitials,
  type EmployeeWithRelations,
  type Department,
  type EmployeeStatus,
  type EmploymentType,
} from "@/lib/supabase/employees-types";
import PageSkeleton from "./PageSkeleton";

const STATUS_COLORS: Record<EmployeeStatus, string> = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  on_leave: "bg-amber-100 text-amber-700 border-amber-200",
  terminated: "bg-red-100 text-red-700 border-red-200",
};

const AVATAR_COLORS = [
  "bg-teal-500", "bg-blue-500", "bg-purple-500",
  "bg-orange-500", "bg-pink-500", "bg-indigo-500",
];

function StatusBadge({ status }: { status: EmployeeStatus }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[status]}`}>
      {EMPLOYEE_STATUS_LABELS[status]}
    </span>
  );
}

function Avatar({ first, last, index }: { first: string; last: string; index: number }) {
  return (
    <div className={`w-8 h-8 rounded-full ${AVATAR_COLORS[index % AVATAR_COLORS.length]} flex items-center justify-center text-white text-xs font-semibold flex-shrink-0`}>
      {getInitials(first, last)}
    </div>
  );
}

export default function EmployeesPage({ onAdd }: { onAdd?: () => void }) {
  const [employees, setEmployees] = useState<EmployeeWithRelations[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchEmployees(), fetchDepartments()])
      .then(([emps, depts]) => {
        if (!cancelled) {
          setEmployees(emps);
          setDepartments(depts);
        }
      })
      .catch(() => { if (!cancelled) setLoading(false); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = employees.filter((emp) => {
    const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
    const matchSearch = !search || fullName.includes(search.toLowerCase()) || emp.email.toLowerCase().includes(search.toLowerCase());
    const matchDept = !deptFilter || emp.department_id === deptFilter;
    const matchStatus = !statusFilter || emp.status === statusFilter;
    return matchSearch && matchDept && matchStatus;
  });

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-bold text-gray-900">Employees</h1>
          <p className="text-gray-500 mt-1">{employees.length} employee{employees.length !== 1 ? "s" : ""}</p>
        </div>
        <Button
          onClick={onAdd || (() => {})}
          className="bg-[#1D9E75] hover:bg-[#1a8b66] text-white gap-2 cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          Add Employee
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10"
          />
        </div>
        <Select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} className="sm:w-48">
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </Select>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="sm:w-40">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="on_leave">On leave</option>
          <option value="terminated">Terminated</option>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <MoreHorizontal className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-gray-500 text-sm">
              {search || deptFilter || statusFilter ? "No employees match your filters" : "No employees yet"}
            </p>
            <p className="text-gray-400 text-xs mt-1">
              {search || deptFilter || statusFilter ? "Try adjusting your filters" : "Add your first employee to get started"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-4">Name</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-4 hidden md:table-cell">Job title</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-4 hidden lg:table-cell">Department</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-4 hidden sm:table-cell">Type</th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-4">Status</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((emp, i) => (
                  <tr
                    key={emp.id}
                    className="hover:bg-gray-50/50 transition-colors duration-150 cursor-pointer"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <Avatar first={emp.first_name} last={emp.last_name} index={i} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{emp.first_name} {emp.last_name}</p>
                          <p className="text-xs text-gray-500">{emp.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 hidden md:table-cell">{emp.job_title || "—"}</td>
                    <td className="py-3 px-4 text-sm text-gray-600 hidden lg:table-cell">{emp.department?.name || "—"}</td>
                    <td className="py-3 px-4 text-sm text-gray-600 hidden sm:table-cell">{EMPLOYMENT_TYPE_LABELS[emp.employment_type]}</td>
                    <td className="py-3 px-4 text-center"><StatusBadge status={emp.status} /></td>
                    <td className="py-3 px-4">
                      {(emp as any).resignation_status === "submitted" && (
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" title="Resignation pending"></span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
